import type { Action, Resource } from '@ukinfo/shared';
import { useEffect } from 'react';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AppShell } from './components/AppShell';
import { EmptyState, LoadingBlock, useToast } from './components/ui';
import { useAuth } from './lib/auth';
import { CalendarPage } from './pages/CalendarPage';
import { ClubPage } from './pages/ClubPage';
import { PersonPage } from './pages/PersonPage';
import { KnowledgePage } from './pages/KnowledgePage';
import { LoginPage } from './pages/LoginPage';
import { MediaPage } from './pages/MediaPage';
import { OfflinePage } from './pages/OfflinePage';
import { SettingsPage } from './pages/SettingsPage';
import { SponsoringPage } from './pages/SponsoringPage';
import { SponsorProfilePage } from './pages/SponsorProfilePage';
import { SponsorshipDetailPage } from './pages/SponsorshipDetailPage';
import { TaskDetailPage } from './pages/TaskDetailPage';
import { TasksPage } from './pages/TasksPage';
import { TodayPage } from './pages/TodayPage';
import { MembersPage } from './pages/MembersPage';
import { FinancePage } from './pages/FinancePage';
import { ExercisesPage } from './pages/ExercisesPage';
import { ExerciseRemarksPage } from './pages/ExerciseRemarksPage';
import { FormationsPage } from './pages/FormationsPage';
import { SportsPage } from './pages/SportsPage';
import { TrainingPlansPage } from './pages/TrainingPlansPage';
import { TeamPage } from './pages/TeamPage';
import { TrainingInvitePage } from './pages/TrainingInvitePage';
import { RegistrationPage } from './pages/RegistrationPage';
import { PollDetailPage, PollsPage } from './pages/PollsPage';
import { PollCreatePage } from './pages/PollCreatePage';

/** Statt stiller Umleitung: Hinweis zeigen und zu Heute zurück. */
function KeineBerechtigung(): JSX.Element {
  const toast = useToast();
  useEffect(() => {
    toast.show('Dafür fehlt dir die Berechtigung', 'error');
  }, []);
  return <Navigate to="/heute" replace />;
}

function NichtGefunden(): JSX.Element {
  return (
    <EmptyState
      title="Diese Seite gibt es nicht"
      description="Der Link ist veraltet oder falsch geschrieben."
      action={<Link to="/heute" className="text-sm font-medium text-brand-700 hover:underline">Zu Heute</Link>}
    />
  );
}

function Protected({
  children,
  resource,
  resources,
  action = 'read',
}: {
  children: JSX.Element;
  resource?: Resource;
  resources?: Resource[];
  action?: Action;
}): JSX.Element {
  const { user, isLoading, networkError, can } = useAuth();
  const location = useLocation();

  if (isLoading) return <LoadingBlock label="Anmeldung wird geprüft …" />;
  // Ohne Netz laesst sich die Sitzung nicht pruefen: dann bleiben die offline gespeicherten Einheiten.
  if (!user && networkError) return <Navigate to="/offline" replace />;
  if (!user) return <Navigate to="/anmelden" state={{ from: location.pathname }} replace />;
  if (resource && !can(resource, action)) return <KeineBerechtigung />;
  if (resources && !resources.some((candidate) => can(candidate, action))) {
    return <KeineBerechtigung />;
  }
  return <AppShell>{children}</AppShell>;
}

export function App(): JSX.Element {
  return (
    <Routes>
      <Route path="/anmelden" element={<LoginPage />} />
      <Route path="/antworten" element={<TrainingInvitePage />} />
      {/* Ohne Anmeldung der Sitzung erreichbar: ohne Netz gibt es nur diese Seite. */}
      <Route path="/offline" element={<OfflinePage />} />
      <Route path="/offline/:planId" element={<OfflinePage />} />
      <Route path="/registrieren" element={<RegistrationPage />} />
      <Route path="/t/:token" element={<TrainingInvitePage />} />
      <Route path="/" element={<Navigate to="/heute" replace />} />

      <Route
        path="/heute"
        element={
          <Protected>
            <TodayPage />
          </Protected>
        }
      />
      <Route
        path="/aufgaben"
        element={
          <Protected resource="tasks">
            <TasksPage />
          </Protected>
        }
      />
      <Route
        path="/aufgaben/:id"
        element={
          // Wer der Aufgabe zugewiesen ist, darf sie auch ohne Leserecht auf Aufgaben öffnen;
          // die API entscheidet und die Seite zeigt deren Fehler.
          <Protected>
            <TaskDetailPage />
          </Protected>
        }
      />
      <Route
        path="/kalender"
        element={
          <Protected resource="calendar">
            <CalendarPage />
          </Protected>
        }
      />
      <Route path="/umfragen" element={<Protected><PollsPage /></Protected>} />
      <Route path="/umfragen/neu" element={<Protected resource="polls" action="create"><PollCreatePage /></Protected>} />
      <Route path="/umfragen/:id" element={<Protected><PollDetailPage /></Protected>} />
      <Route path="/sport" element={<Protected resources={['matches', 'sports_participation', 'sports_attendance', 'sports_reports']}><SportsPage /></Protected>} />
      <Route path="/sport/uebungen" element={<Protected resource="sports_training_plans"><ExercisesPage /></Protected>} />
      <Route path="/sport/uebungen/:id" element={<Protected resource="sports_training_plans"><ExercisesPage /></Protected>} />
      <Route path="/sport/einheiten" element={<Protected resource="sports_training_plans"><TrainingPlansPage /></Protected>} />
      <Route path="/sport/formationen" element={<Protected resource="sports_training_plans"><FormationsPage /></Protected>} />
      <Route path="/sport/formationen/:id" element={<Protected resource="sports_training_plans"><FormationsPage /></Protected>} />
      <Route path="/sport/hinweise" element={<Protected resource="sports_training_plans" action="manage"><ExerciseRemarksPage /></Protected>} />
      <Route path="/sport/:teamId" element={<Protected resources={['matches', 'sports_participation', 'sports_attendance', 'sports_reports']}><TeamPage /></Protected>} />
      <Route path="/mitglieder" element={<Protected resource="persons"><MembersPage /></Protected>} />
      <Route path="/finanzen" element={<Protected resource="finance"><FinancePage /></Protected>} />
      <Route
        path="/spiele"
        element={
          <Protected resource="matches">
            <CalendarPage initialTab="matches" />
          </Protected>
        }
      />
      <Route
        path="/spiele/:id"
        element={
          <Protected resource="matches">
            <CalendarPage initialTab="matches" />
          </Protected>
        }
      />
      <Route
        path="/sponsoring"
        element={
          <Protected resource="sponsoring">
            <SponsoringPage />
          </Protected>
        }
      />
      <Route
        path="/sponsoring/vertraege/:id"
        element={
          <Protected resource="sponsoring">
            <SponsorshipDetailPage />
          </Protected>
        }
      />
      <Route
        path="/sponsoring/:sponsorId"
        element={
          <Protected resource="sponsoring">
            <SponsorProfilePage />
          </Protected>
        }
      />
      <Route
        path="/medien"
        element={
          <Protected resource="media">
            <MediaPage />
          </Protected>
        }
      />
      <Route
        path="/medien/beitraege/:id"
        element={
          <Protected resource="media">
            <MediaPage />
          </Protected>
        }
      />
      <Route
        path="/wissen"
        element={
          <Protected resource="documents">
            <KnowledgePage />
          </Protected>
        }
      />
      <Route
        path="/wissen/:id"
        element={
          <Protected resource="documents">
            <KnowledgePage />
          </Protected>
        }
      />
      <Route
        path="/verein/*"
        element={
          <Protected resources={['organisation', 'persons', 'permissions', 'tasks', 'calendar']}>
            <ClubPage />
          </Protected>
        }
      />
      <Route
        path="/personen/:id"
        element={
          <Protected resource="persons">
            <PersonPage />
          </Protected>
        }
      />
      <Route
        path="/einstellungen"
        element={
          <Protected>
            <SettingsPage />
          </Protected>
        }
      />

      <Route path="*" element={<Protected><NichtGefunden /></Protected>} />
    </Routes>
  );
}
